import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError, api, money, useQuery } from '../api.js';
import { useSession } from '../session.js';
import { Drawer, FindingList, Pane, Panes, Pill, Spinner, useToast } from '../ui.js';
import { Segmented } from '../controls.js';
import type { QueuedRequest } from './RequestQueue.js';
import type { Finding, Slot } from '../../../shared/types.js';

interface Resources {
  aircraft: { id: number; tail: string; model: string; grounded: boolean }[];
  instructors: { id: number; name: string; ratings: string }[];
  today: string;
}
interface StudentRow {
  id: number; name: string; courseName: string; regulation: string; stage: number;
  primaryInstructorId: number | null;
}

interface LessonOption {
  id: number; code: string; name: string; stage: number; seq: number;
  flightHours: number; groundHours: number;
  isStageCheck: boolean; isSolo: boolean; isGround: boolean; isRemedial: boolean;
}
interface Catalog {
  courses: { id: number; name: string; shortName: string; regulation: string; certificate: string; lessons: LessonOption[] }[];
  activities: { id: string; label: string; kind: string; note: string }[];
  /** Part 141 requires every student session to be a lesson from the course. */
  requiresLesson: boolean;
}
interface NextLesson {
  course: { id: number; name: string; regulation: string } | null;
  lesson: (LessonOption & { objective: string; isNight: boolean; isXc: boolean; requiresIfr: boolean }) | null;
  remedial: (LessonOption & { because: string }) | null;
  gate: { title: string; body: string } | null;
}


/**
 * Making a reservation. Opened from the schedule rather than living on a tab of
 * its own, and opened with a waiting request when one is being assigned.
 */
/** The calendar day an instant falls on locally, as YYYY-MM-DD. */
function localDate(ms: number): string {
  return new Date(ms).toLocaleDateString('en-CA');
}

/**
 * Which part of the day a requested time sits in, so the slot search opens
 * around when they asked rather than at the start of the working day.
 */
function partOfDayFor(ms: number): string {
  const hour = new Date(ms).getHours();
  if (hour < 12) return 'Morning';
  if (hour < 15) return 'Midday';
  if (hour < 18) return 'Afternoon';
  return 'Evening';
}

export function NewReservation({ request, onClose, onDone }: {
  request?: QueuedRequest | null;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const { can, session } = useSession();
  const toast = useToast();
  const navigate = useNavigate();

  const resources = useQuery<Resources>('/schedule/resources');
  const roster = useQuery<{ students: StudentRow[] }>('/students');
  const catalog = useQuery<Catalog>('/schedule/catalog');

  const [studentId, setStudentId] = useState<number | null>(request?.studentId ?? null);
  const [lessonId, setLessonId] = useState<number | null>(null);
  const [activity, setActivity] = useState<string>('flight-instruction');
  const [mode, setMode] = useState<'syllabus' | 'activity'>('syllabus');
  // Set once the instructor picks a lesson themselves, so autoload stops overriding it.
  const [lessonTouched, setLessonTouched] = useState(false);
  // Same for the instructor: chosen by hand, left alone thereafter.
  const [instructorTouched, setInstructorTouched] = useState(false);
  const [date, setDate] = useState('');
  const [duration, setDuration] = useState(2);
  const [aircraftId, setAircraftId] = useState<number | null>(null);
  const [instructorId, setInstructorId] = useState<number | null>(null);
  const [partOfDay, setPartOfDay] = useState('Any');
  const [picked, setPicked] = useState<Slot | null>(null);
  const [overrideReason, setOverrideReason] = useState('');
  const [busy, setBusy] = useState(false);
  // Set when an existing request is being assigned rather than a new booking made.
  const assigning = request ?? null;

  const nextLesson = useQuery<NextLesson>(studentId ? `/students/${studentId}/next-lesson` : null);

  // Default the day to tomorrow, and the lesson and duration to the student's syllabus.
  useEffect(() => {
    if (!date && resources.data) setDate(shift(resources.data.today, 1));
  }, [resources.data, date]);

  // Selecting a student loads their course and drops onto the next lesson in it.
  useEffect(() => {
    if (assigning) return;
    setLessonTouched(false);
    setMode('syllabus');
  }, [studentId, assigning]);

  /**
   * Who would teach this. A CFI booking is almost always booking themselves,
   * and a lesson for a student is almost always with the instructor who teaches
   * them — so both are filled in and neither is imposed once somebody has
   * chosen for themselves.
   */
  useEffect(() => {
    if (assigning || instructorTouched) return;
    const student = roster.data?.students.find((s) => s.id === studentId);
    const suggested = student?.primaryInstructorId ?? session?.instructorId ?? null;
    if (suggested != null) setInstructorId(suggested);
  }, [studentId, roster.data, assigning, instructorTouched, session?.instructorId]);

  /**
   * A request opened for assignment arrives from the server a moment after this
   * form does, so everything it carries is filled in here rather than in the
   * initial state — which has already been read by the time it lands.
   */
  useEffect(() => {
    if (!request) return;
    // Who asked. Without this the form opens with no student and the lesson
    // lookup below has nothing to work from.
    setStudentId(request.studentId);
    setLessonId(request.lessonId);
    setLessonTouched(true);
    setMode(request.lessonId ? 'syllabus' : 'activity');
    if (request.activity) setActivity(request.activity);
    // The day they asked for, read on the wall clock rather than in UTC: an
    // evening lesson in the Americas is already tomorrow by UTC, and offering
    // to book it a day late is worse than not offering at all.
    setDate(localDate(request.startsAt));
    setDuration(Math.max(0.5, Math.round(((request.endsAt - request.startsAt) / 3600000) * 2) / 2));
    // Whatever they had already been given keeps its place; what is missing is
    // exactly what this screen is open to decide.
    setInstructorId(request.instructorId);
    setAircraftId(request.aircraftId);
    setPartOfDay(partOfDayFor(request.startsAt));
  }, [request]);

  useEffect(() => {
    if (lessonTouched) return;
    const l = nextLesson.data?.lesson;
    if (l) {
      setLessonId(l.id);
      setDuration(Math.max(1, Math.round((l.flightHours + 0.4) * 2) / 2));
    } else {
      setLessonId(null);
    }
    setPicked(null);
  }, [nextLesson.data, lessonTouched]);

  const slotPath = useMemo(() => {
    if (!date) return null;
    const q = new URLSearchParams({ date, durationHours: String(duration), partOfDay });
    if (studentId) q.set('studentId', String(studentId));
    if (mode === 'syllabus' && lessonId) q.set('lessonId', String(lessonId));
    if (aircraftId) q.set('aircraftId', String(aircraftId));
    if (instructorId) q.set('instructorId', String(instructorId));
    return `/booking/slots?${q}`;
  }, [date, duration, partOfDay, studentId, lessonId, mode, aircraftId, instructorId]);

  const slots = useQuery<{ slots: Slot[] }>(slotPath);

  const student = roster.data?.students.find((s) => s.id === studentId);
  const courseId = nextLesson.data?.course?.id;
  const courseLessons = useMemo(
    () => catalog.data?.courses.find((c) => c.id === courseId)?.lessons ?? [],
    [catalog.data, courseId],
  );
  const conflicts = picked?.findings.filter((f) => f.severity === 'conflict') ?? [];
  const reviews = picked?.findings.filter((f) => f.severity === 'review') ?? [];

  const estimate = useMemo(() => {
    if (!picked) return null;
    // A quick figure for the summary rail; the invoice is priced from the Hobbs.
    const hrs = (picked.endsAt - picked.startsAt) / 3600000;
    return Math.round(hrs * 17000 + hrs * 6000 + 0.5 * 6000);
  }, [picked]);

  /** Assign the chosen slot to the waiting request and confirm it. */
  const assign = async () => {
    if (!picked || !assigning) return;
    setBusy(true);
    try {
      await api.patch(`/schedule/reservations/${assigning.id}`, {
        aircraftId: picked.aircraftId,
        instructorId: picked.instructorId,
        lessonId: mode === 'syllabus' ? lessonId : null,
        activity: mode === 'activity' ? activity : null,
        startsAt: picked.startsAt,
        endsAt: picked.endsAt,
        overrideReason: overrideReason || undefined,
      });
      await api.post(`/schedule/reservations/${assigning.id}/status`, { status: 'confirmed' });
      toast.ok('Request assigned and confirmed', `${assigning.studentName || 'The request'} is on the board.`);
      onDone();
      navigate(`/schedule?date=${date}&reservation=${assigning.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.findings?.length) {
        toast.error('Blocked by a conflict', err.findings.map((f) => f.title).join(' · '));
      } else {
        toast.error('Could not assign that request', err instanceof Error ? err.message : undefined);
      }
      setBusy(false);
    }
  };

  const submit = async (status: 'confirmed' | 'held') => {
    if (!picked) return;
    setBusy(true);
    try {
      const chosenLesson = courseLessons.find((l) => l.id === lessonId);
      const chosenActivity = catalog.data?.activities.find((a) => a.id === activity);
      const kind = mode === 'activity'
        ? (chosenActivity?.kind ?? 'dual')
        : chosenLesson?.isSolo ? 'solo' : chosenLesson?.isStageCheck ? 'checkride' : 'dual';

      const result = await api.post<{ reservation: { id: number } }>('/schedule/reservations', {
        kind,
        status,
        aircraftId: picked.aircraftId,
        instructorId: picked.instructorId,
        studentId,
        lessonId: mode === 'syllabus' ? lessonId : null,
        activity: mode === 'activity' ? activity : null,
        startsAt: picked.startsAt,
        endsAt: picked.endsAt,
        groundHours: (mode === 'syllabus' ? chosenLesson?.groundHours : undefined) ?? 0.5,
        holdMinutes: status === 'held' ? 30 : undefined,
        overrideReason: overrideReason || undefined,
      });
      toast.ok(status === 'held' ? 'Slot held for 30 minutes' : 'Reservation confirmed');
      onDone();
      navigate(`/schedule?date=${date}&reservation=${result.reservation.id}`);
    } catch (err) {
      if (err instanceof ApiError && err.findings?.length) {
        toast.error('Blocked by a conflict', err.findings.map((f) => f.title).join(' · '));
      } else {
        toast.error('Could not book that', err instanceof Error ? err.message : undefined);
      }
    } finally {
      setBusy(false);
    }
  };

  const step = !studentId ? 0 : picked ? 2 : 1;

  if (resources.loading || roster.loading) {
    return <Drawer title="New reservation" onClose={onClose}><Spinner /></Drawer>;
  }

  return (
    <Drawer
      title={assigning ? 'Assign a request' : 'New reservation'}
      subtitle={assigning
        ? `${assigning.studentName || 'Request'}${assigning.lessonLabel ? ` · ${assigning.lessonLabel}` : ''}`
        : 'Checked against maintenance, duty policy, currency and syllabus order'}
      onClose={onClose}
    >
      <Panes>
      <Pane flush>
      <div>
        {assigning && (
          <div style={{
            margin: '14px 18px 0', padding: '11px 13px', borderRadius: 'var(--r-md)',
            background: 'var(--tint-blue)', fontSize: 12.5, textWrap: 'pretty',
          }}>
            Picking a slot below assigns it to this request and confirms it, rather than
            creating a second reservation.
          </div>
        )}

        {/* Part 141 requires a syllabus lesson; elsewhere a session may be
            anything the instructor and student agree on. */}
        {!catalog.data?.requiresLesson && (
          <div style={{ padding: '12px 12px 0' }}>
            <Segmented
              value={mode}
              onChange={(m) => { setMode(m); setPicked(null); }}
              label="What is this session?"
              options={[
                { value: 'syllabus', label: 'Syllabus lesson' },
                { value: 'activity', label: 'Other activity' },
              ]}
            />
          </div>
        )}

        <div style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 10, borderBottom: '1px solid var(--border-3)' }}>
          <label className="field">
            <span>Student</span>
            <select className="select" value={studentId ?? ''} onChange={(e) => setStudentId(e.target.value ? Number(e.target.value) : null)}>
              <option value="">Select a student…</option>
              {roster.data?.students.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Course</span>
            <input className="input" readOnly
              value={nextLesson.data?.course
                ? `${nextLesson.data.course.name} · Part ${nextLesson.data.course.regulation}`
                : student?.courseName ?? '—'} />
          </label>

          {mode === 'syllabus' ? (
            <label className="field">
              <span>Lesson</span>
              <select
                className="select"
                value={lessonId ?? ''}
                disabled={!courseLessons.length}
                onChange={(e) => {
                  setLessonTouched(true);
                  const id = e.target.value ? Number(e.target.value) : null;
                  setLessonId(id);
                  const l = courseLessons.find((x) => x.id === id);
                  if (l) setDuration(Math.max(1, Math.round((l.flightHours + 0.4) * 2) / 2));
                  setPicked(null);
                }}
              >
                {!courseLessons.length && <option value="">No course syllabus</option>}
                {courseLessons.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.code} · {l.name}
                    {l.id === nextLesson.data?.lesson?.id ? '  (next up)' : ''}
                    {l.isRemedial ? '  (remedial)' : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : (
            <label className="field">
              <span>Activity</span>
              <select className="select" value={activity}
                onChange={(e) => { setActivity(e.target.value); setPicked(null); }}>
                {catalog.data?.activities.map((a) => (
                  <option key={a.id} value={a.id}>{a.label}</option>
                ))}
              </select>
            </label>
          )}
          <label className="field">
            <span>Duration</span>
            <select className="select" value={duration} onChange={(e) => { setDuration(Number(e.target.value)); setPicked(null); }}>
              {[1, 1.5, 2, 2.5, 3, 3.5, 4].map((h) => <option key={h} value={h}>{h.toFixed(1)} hrs</option>)}
            </select>
          </label>
        </div>

        <div style={{ padding: 12, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(160px, 100%), 1fr))', gap: 10, borderBottom: '1px solid var(--border-3)' }}>
          <label className="field">
            <span>Date</span>
            <input className="input" type="date" value={date} onChange={(e) => { setDate(e.target.value); setPicked(null); }} />
          </label>
          <label className="field">
            <span>Time of day</span>
            <select className="select" value={partOfDay} onChange={(e) => { setPartOfDay(e.target.value); setPicked(null); }}>
              {['Any', 'Morning', 'Midday', 'Afternoon', 'Evening'].map((p) => <option key={p}>{p}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Aircraft</span>
            <select className="select" value={aircraftId ?? ''} onChange={(e) => { setAircraftId(e.target.value ? Number(e.target.value) : null); setPicked(null); }}>
              <option value="">Any available</option>
              {resources.data?.aircraft.map((a) => (
                <option key={a.id} value={a.id} disabled={a.grounded}>
                  {a.tail} · {a.model}{a.grounded ? ' (grounded)' : ''}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Instructor</span>
            <select className="select" value={instructorId ?? ''} onChange={(e) => {
              setInstructorTouched(true);
              setInstructorId(e.target.value ? Number(e.target.value) : null);
              setPicked(null);
            }}>
              <option value="">Any available</option>
              {resources.data?.instructors.map((i) => <option key={i.id} value={i.id}>{i.name}</option>)}
            </select>
          </label>
        </div>

        {mode === 'syllabus' && nextLesson.data?.remedial && lessonId !== nextLesson.data.remedial.id && (
          <div style={{
            margin: '0 12px 12px', padding: '11px 13px', borderRadius: 'var(--r-md)',
            background: 'var(--tint-orange)', color: 'var(--k-pending-fg)', fontSize: 12.5,
          }}>
            <strong style={{ display: 'block' }}>{nextLesson.data.remedial.because}</strong>
            <span style={{ textWrap: 'pretty' }}>
              {nextLesson.data.remedial.code} {nextLesson.data.remedial.name} is available to work it off.
            </span>{' '}
            <button className="btn btn--link" onClick={() => {
              setLessonTouched(true);
              setLessonId(nextLesson.data!.remedial!.id);
              setDuration(Math.max(1, Math.round((nextLesson.data!.remedial!.flightHours + 0.4) * 2) / 2));
              setPicked(null);
            }}>Schedule it</button>
          </div>
        )}

        <div className="card__head">
          <span className="card__title">Available slots · {date ? longDate(date) : '—'}</span>
          <span className="card__meta">Checked against maintenance, duty policy, currency and syllabus order</span>
        </div>

        {slots.loading && <Spinner label="Checking the fleet…" />}
        {!slots.loading && slots.data?.slots.length === 0 && (
          <div className="empty">
            Nothing open with those filters. Try another day, a shorter block, or any aircraft.
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(238px, 100%), 1fr))' }}>
          {slots.data?.slots.map((s) => {
            const on = picked?.startsAt === s.startsAt && picked?.aircraftId === s.aircraftId;
            return (
              <button
                key={`${s.startsAt}-${s.aircraftId}`}
                onClick={() => { setPicked(s); setOverrideReason(''); }}
                style={{
                  padding: '11px 12px', textAlign: 'left', border: 0,
                  borderRight: '1px solid var(--border-3)', borderBottom: '1px solid var(--border-3)',
                  background: on ? 'var(--accent-tint)' : s.tag === 'Best fit' ? 'var(--accent-tint-2)' : 'var(--surface)',
                  boxShadow: on ? 'inset 0 0 0 2px var(--accent)' : 'none',
                }}
              >
                <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 14.5 }}>{time(s.startsAt)} – {time(s.endsAt)}</span>
                  <Pill>{s.tag}</Pill>
                </span>
                <span style={{ display: 'block', fontSize: 12, marginTop: 3 }}>{s.aircraftTail} · {s.aircraftModel}</span>
                <span style={{ display: 'block', fontSize: 11.5, marginTop: 1 }}>{s.instructorName}</span>
                <span className="small muted" style={{ display: 'block', marginTop: 2, textWrap: 'pretty' }}>{s.note}</span>
              </button>
            );
          })}
        </div>
      </div>
      </Pane>

      <Pane title={assigning ? 'Assigning a request' : 'Reservation summary'} flush>
        <div className="kv"><span>Student</span><span>{student?.name ?? '—'}</span></div>
        <div className="kv"><span>Course</span><span>{student?.courseName ?? '—'}</span></div>
        <div className="kv">
          <span>{mode === 'activity' ? 'Activity' : 'Lesson'}</span>
          <span>
            {mode === 'activity'
              ? catalog.data?.activities.find((a) => a.id === activity)?.label ?? '—'
              : courseLessons.find((l) => l.id === lessonId)?.code ?? '—'}
          </span>
        </div>
        <div className="kv"><span>Aircraft</span><span>{picked?.aircraftTail ?? '—'}</span></div>
        <div className="kv"><span>Instructor</span><span>{picked?.instructorName ?? '—'}</span></div>
        <div className="kv"><span>Block</span><span>{picked ? `${((picked.endsAt - picked.startsAt) / 3600000).toFixed(1)} hrs` : '—'}</span></div>
        <div className="kv"><span>Estimate</span><span>{estimate ? money(estimate) : '—'}</span></div>

        {nextLesson.data?.gate && (
          <div style={{ margin: 10, padding: 8, background: 'var(--conflict-bg)', border: '1px solid var(--conflict-bd)', borderRadius: 6 }}>
            <div style={{ fontWeight: 700, fontSize: 12, color: 'var(--conflict-c)' }}>{nextLesson.data.gate.title}</div>
            <div className="small" style={{ color: 'var(--conflict-c)', marginTop: 2, textWrap: 'pretty' }}>{nextLesson.data.gate.body}</div>
          </div>
        )}

        {picked && (conflicts.length > 0 || reviews.length > 0) && (
          <>
            <div className="card__head" style={{ borderTop: '1px solid var(--border-2)' }}>
              <span className="card__title">
                {conflicts.length ? `${conflicts.length} conflict${conflicts.length === 1 ? '' : 's'}` : `${reviews.length} item${reviews.length === 1 ? '' : 's'} need review`}
              </span>
            </div>
            <FindingList findings={picked.findings as Finding[]} />
          </>
        )}

        <div style={{ padding: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {conflicts.length > 0 && can('reservation:override') && (
            <label className="field">
              <span>Override reason (recorded on the reservation)</span>
              <textarea className="textarea" style={{ minHeight: 56 }} value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                placeholder="Why this flight goes ahead despite the conflict" />
            </label>
          )}
          <button
            className="btn btn--primary btn--block"
            disabled={!picked || busy || (conflicts.length > 0 && (!can('reservation:override') || overrideReason.trim().length < 5))}
            onClick={() => (assigning ? assign() : submit('confirmed'))}
          >
            {assigning
              ? conflicts.length ? 'Assign with override' : 'Assign and confirm'
              : conflicts.length ? 'Confirm with override' : 'Confirm reservation'}
          </button>
          {!assigning && (
            <button className="btn btn--block" disabled={!picked || busy || conflicts.length > 0} onClick={() => submit('held')}>
              Hold slot for 30 minutes
            </button>
          )}
          {conflicts.length > 0 && !can('reservation:override') && (
            <span className="small muted">Only management can book past a conflict.</span>
          )}
        </div>
      </Pane>
      </Panes>
    </Drawer>
  );
}

function shift(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}
function longDate(date: string): string {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'long', timeZone: 'UTC' });
}
function time(ms: number): string {
  return new Date(ms).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
}
